/**
 * The document head for the how-to guides.
 *
 * ── Why there is no `HowTo` markup ───────────────────────────────────────────
 *
 * schema.org has a `HowTo` type and Google withdrew its rich result for it in
 * 2023, so it buys nothing on the results page. That is the small reason. The
 * larger one is that `HowTo` asserts the page is a procedure for achieving the
 * thing in its name, and on three of these five pages the honest answer is that
 * the thing cannot be achieved — `get-hunter-eyes` marked up as a `HowTo` would
 * be structured data claiming the opposite of the page's first paragraph.
 *
 * So: `FAQPage` for the questions, `BreadcrumbList` for the trail, and nothing
 * that makes a claim the prose spends its length refuting.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import {
  alternates,
  breadcrumbJsonLd,
  canonical,
  faqJsonLd,
  organizationJsonLd,
  socialMeta,
} from "../seo";
import { HOWTO_SLUGS, howtoIndexPath, howtoPath } from "./index";
import type { HowtoBundle, HowtoGuide, HowtoSlug } from "./types";
import type { HowtoUi } from "./ui";

export function howtoHead(
  slug: HowtoSlug,
  lang: ContentLang,
  guide: HowtoGuide,
  ui: HowtoUi,
) {
  const path = howtoPath(slug, lang);
  return {
    meta: [
      { title: guide.title },
      { name: "description", content: guide.description },
      ...socialMeta({
        title: guide.title,
        description: guide.description,
        path,
        lang,
        imageAlt: guide.h1,
      }),
      { "script:ld+json": faqJsonLd([...guide.faq]) },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path: howtoIndexPath(lang) },
          { name: guide.h1, path },
        ]),
      },
      { "script:ld+json": organizationJsonLd() },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates((l) => howtoPath(slug, l)),
    ],
  };
}

export function howtoIndexHead(
  lang: ContentLang,
  ui: HowtoUi,
  bundle: HowtoBundle,
) {
  const path = howtoIndexPath(lang);
  return {
    meta: [
      { title: ui.indexTitle },
      { name: "description", content: ui.indexDescription },
      ...socialMeta({
        title: ui.indexTitle,
        description: ui.indexDescription,
        path,
        lang,
        imageAlt: ui.indexH1,
      }),
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: ui.indexH1,
          description: ui.indexDescription,
          url: canonical(path),
          inLanguage: lang,
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: HOWTO_SLUGS.length,
            itemListElement: HOWTO_SLUGS.map((slug, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: bundle[slug].h1,
              url: canonical(howtoPath(slug, lang)),
            })),
          },
        },
      },
      {
        "script:ld+json": breadcrumbJsonLd([
          { name: "Fazly", path: lang === "en" ? "/" : `/${lang}` },
          { name: ui.indexH1, path },
        ]),
      },
    ],
    links: [
      { rel: "canonical", href: canonical(path) },
      ...alternates(howtoIndexPath),
    ],
  };
}
